import { useState, useEffect, useRef, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../firebase/config';
import { loadLatestEntityFormationDraft, loadEntityFormationDraftById, saveEntityFormationDraft } from '../lib/intake/entityFormationDraftApi';
import type { EntityIntakeDefaults } from '../lib/intake/entityFormationDefaults';

/**
 * Hook return type
 */
export interface UseEntityFormationDraftReturn {
  isLoading: boolean;
  draftId: string | null;
  lastSavedAt: Timestamp | null;
  saveNow: () => Promise<void>;
  reloadDraft: () => Promise<void>;
}

/**
 * Custom hook for managing entity formation draft
 * - Loads draft on mount if user is authenticated
 * - Tracks dirty state and autosaves every 10 seconds when dirty
 * - Exposes saveNow() for manual saves (e.g., on step change)
 * - Never autosaves while initial load is in progress
 * 
 * @param data - Current form data
 * @param setData - Function to update form data (used to hydrate form when draft loads)
 * @param specificDraftId - Optional specific draft ID to load instead of the latest draft
 */
export function useEntityFormationDraft(
  data: EntityIntakeDefaults,
  setData: (data: EntityIntakeDefaults) => void,
  specificDraftId?: string | null
): UseEntityFormationDraftReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Timestamp | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Track initial load to prevent autosave during load
  const isInitialLoadRef = useRef(true);
  const previousDataRef = useRef<EntityIntakeDefaults | null>(null);
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Load draft from Firestore
   */
  const reloadDraft = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user?.uid || !user?.email) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      isInitialLoadRef.current = true;
      
      let result;
      if (specificDraftId) {
        // Load specific draft by ID (Firestore security rules will ensure user owns it)
        result = await loadEntityFormationDraftById(specificDraftId);
        if (result) {
          result = {
            draftId: result.draftId,
            data: result.data,
            updatedAt: result.updatedAt,
          };
        }
      } else {
        // Load latest draft
        result = await loadLatestEntityFormationDraft(user.uid);
      }
      
      if (result) {
        setDraftId(result.draftId);
        setLastSavedAt(result.updatedAt);
        
        // Hydrate form state
        setData(result.data);
        
        // Set as previous data for comparison
        previousDataRef.current = result.data;
        setIsDirty(false);
      } else {
        setDraftId(specificDraftId || null);
        setLastSavedAt(null);
        previousDataRef.current = null;
        setIsDirty(false);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      // If loading specific draft failed, set the draftId anyway so user can continue editing
      if (specificDraftId) {
        setDraftId(specificDraftId);
      }
    } finally {
      setIsLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [setData, specificDraftId]);

  /**
   * Save draft to Firestore
   */
  const saveNow = useCallback(async (): Promise<void> => {
    const user = auth.currentUser;
    if (!user?.uid || !user?.email) {
      return;
    }

    // Don't save if still loading
    if (isInitialLoadRef.current) {
      return;
    }

    try {
      const result = await saveEntityFormationDraft({
        uid: user.uid,
        email: user.email,
        draftId: draftId || undefined,
        data,
      });

      setDraftId(result.draftId);
      setLastSavedAt(result.updatedAt);
      setIsDirty(false);
      previousDataRef.current = data;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  }, [draftId, data]);

  // Load draft on mount if user is authenticated
  // Also reload if specificDraftId changes
  useEffect(() => {
    if (auth.currentUser) {
      reloadDraft();
    } else {
      setIsLoading(false);
    }
  }, [reloadDraft, specificDraftId]);

  // Track dirty state by comparing current data with previous
  useEffect(() => {
    // Skip dirty check during initial load
    if (isInitialLoadRef.current) {
      return;
    }

    const currentDataStr = JSON.stringify(data);
    const previousDataStr = JSON.stringify(previousDataRef.current);
    
    if (currentDataStr !== previousDataStr) {
      setIsDirty(true);
    }
  }, [data]);

  // Autosave every 10 seconds if dirty (but not during initial load)
  useEffect(() => {
    // Don't autosave if still loading or not dirty
    if (isInitialLoadRef.current || !isDirty) {
      // Clear any existing timer
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      return;
    }

    const user = auth.currentUser;
    if (!user?.uid) {
      return;
    }

    // Clear existing timer
    if (autosaveTimerRef.current) {
      clearInterval(autosaveTimerRef.current);
    }

    // Set up new timer
    autosaveTimerRef.current = setInterval(() => {
      // Double-check conditions before saving
      if (!isInitialLoadRef.current && isDirty && user?.uid) {
        saveNow().catch((error) => {
          console.error('Autosave failed:', error);
        });
      }
    }, 10000); // 10 seconds

    // Cleanup
    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [isDirty, saveNow]);

  return {
    isLoading,
    draftId,
    lastSavedAt,
    saveNow,
    reloadDraft,
  };
}

